/**
 * The transcript, flattened into what the thread actually draws.
 *
 * A run of tool calls arrives as several messages - the calls, and the
 * tool_result messages that fold into them - and rendered literally that is a
 * wall of bordered blocks. So the RUN is the unit, not the message: consecutive
 * calls collapse into ONE row regardless of how many messages they came from.
 *
 * What the agent SAYS is never part of that. Text between calls keeps its own row
 * and ends the run, so a turn reads the way it happened: said, did, said. Folding
 * the narration into the group (which an earlier revision did, to make runs
 * longer) bought a tidier thread by deleting the half of it worth reading - the
 * turn became one collapsed row and a conclusion, with every "now the tests"
 * hidden behind a disclosure.
 *
 * The runs are long enough without it: the worker keeps consecutive calls in one
 * message and starts a new one when the agent speaks again, so a batch of six
 * greps is six calls in a row with nothing between them to break the group.
 *
 * AN EDIT IS THE OTHER EXCEPTION (RFC 0298 §4f). Six greps are six ways of
 * asking one question and collapse to a count without losing anything. A call
 * that changed a file is the thing that happened, and it gets its own row so its
 * diff is reachable without opening a group first.
 *
 * Everything here is pure, over the projection, so the grouping rule can be
 * tested without rendering.
 */

import type { TranscriptMessage } from '@mod/core/model'
import { isEdit } from './editDiff'
import { hideQuietTurns, isCommentEvent, passedComments } from './quietTurns'
import { cardForCall, cardForEvent, type ThreadCard } from './threadCards'
import { argString } from './toolArgs'
import { startsTurn } from './turns'

/** Derived rather than imported: mod-core exports the message, not its blocks. */
type TranscriptBlock = TranscriptMessage['content'][number]

export interface ToolCall {
  id: string
  name: string
  arguments: unknown
}

/**
 * `at` is the transcript index of the message that created the row. A turn's
 * Changes card is placed after the last row its own messages created, so it
 * stays with its run whatever is drawn before the next prompt.
 */
export type ThreadRow = ThreadRowBody & { at?: number }

type ThreadRowBody =
  | { kind: 'message'; key: string; message: TranscriptMessage }
  /**
   * A comment that reached this session, drawn as the comment itself (RFC 0324
   * 3d): who wrote it, the passage, the words, and the way back to it. `passed`
   * is the agent's note when it read the comment and chose not to answer.
   */
  | {
      kind: 'comment'
      key: string
      message: TranscriptMessage
      passed?: string
    }
  | { kind: 'tools'; key: string; calls: ToolCall[] }
  /**
   * Lines said on a voice call (RFC 0307 3c), drawn as one quiet group. Lines
   * in a row that share a call join it; anything drawn between them, the
   * agent's work for instance, starts a new group for the same call.
   */
  | { kind: 'call'; key: string; callId: string; lines: SpokenLine[] }
  /**
   * A thing the turn produced or received, drawn as itself (see threadCards).
   *
   * ITS OWN ROW, AND IT ENDS THE RUN, for the same reason the agent's prose
   * does. A card folded into the group beside it is a card nobody sees: the
   * group is closed by default, which is exactly right for eleven greps and
   * exactly wrong for the one call that opened a session. Ending the run costs
   * a row and buys the thing being visible without a click.
   *
   * `call` is carried so the underlying call stays inspectable — a card is a
   * better view of it, not a cover over its arguments.
   */
  | {
      kind: 'card'
      key: string
      card: ThreadCard
      call?: ToolCall
      /** The event behind a card that no call produced: a session that
       *  stopped, a reply. Lets a thread find what was decided about it. */
      message?: TranscriptMessage
      /**
       * Further cards of the SAME kind that landed right after this one, folded
       * into it: eleven edits in a row are one "Edited 11 documents" card, not
       * eleven boxes. `card` and `call` stay the first, so a group of one is
       * exactly the row it always was.
       */
      more?: {
        card: ThreadCard
        call?: ToolCall
        message?: TranscriptMessage
      }[]
    }

/**
 * How many stop reports in a row fold into one group (RFC 0348 3e). Fewer
 * stay cards of their own: two cards are easier to read than a group of two.
 */
export const STOP_GROUP_MIN = 3

const isStopReport = (m: TranscriptMessage | undefined): boolean =>
  m?.kind === 'session_stopped'

export interface SpokenLine {
  key: string
  by: 'person' | 'voice'
  text: string
  /** The voice sent this line to the agent. */
  relayed: boolean
  homeSessionId?: string
  homeEntryId?: string
  targetSessionId?: string
}

/** A tool call's result, as `Thread` already indexes them by `toolUseId`. */
export type ResultLookup = (
  toolUseId: string,
) => { text: string; isError?: boolean } | undefined

/** Each call's result, by `toolUseId`, so results fold into their call. */
export function toolResults(
  transcript: readonly TranscriptMessage[],
): Map<string, { text: string; isError?: boolean }> {
  const map = new Map<string, { text: string; isError?: boolean }>()
  for (const m of transcript) {
    if (m.role === 'tool_result' && m.toolUseId) {
      const text = m.content
        .map((b) => (b.type === 'text' ? b.text : ''))
        .join('')
      map.set(m.toolUseId, {
        text,
        ...(m.isError ? { isError: m.isError } : {}),
      })
    }
  }
  return map
}

export function buildThreadRows(
  transcript: TranscriptMessage[],
  /**
   * Results, for the cards. Whether a document was created and what id it got
   * lives in the result and nowhere else, so without this every card is
   * PENDING — which is what a transcript mid-turn honestly is, and why the
   * parameter is optional rather than required.
   */
  resultFor?: ResultLookup,
): ThreadRow[] {
  const rows: ThreadRow[] = []
  let messageKey = 0
  let cardKey = 0
  /**
   * Rows before this index belong to an earlier turn, and nothing joins them.
   * A turn whose prompt draws no row (an `edited` event, a hidden hand-back)
   * would otherwise pour its calls into the last turn's open run, and that
   * turn's Changes card would sit below another turn's work.
   */
  let floor = 0
  const joinable = () => rows.length > floor

  // One assistant message split around its tool calls draws several text
  // rows. Each needs its own key, or a card placed after one is drawn after all.
  const segments = new Map<string, number>()
  const pushMessage = (message: TranscriptMessage) => {
    let key = `m${messageKey++}`
    if (message.entryId) {
      const n = segments.get(message.entryId) ?? 0
      segments.set(message.entryId, n + 1)
      key = n === 0 ? `m${message.entryId}` : `m${message.entryId}.${n}`
    }
    rows.push({ kind: 'message', key, message })
  }

  /**
   * True while the last row is a run that nothing further may join: an edit,
   * which is a row of its own on both sides (RFC 0298 §4f).
   *
   * A flag rather than re-inspecting `rows.at(-1)`, because "is this row open
   * for more calls" is not answerable from the row: a one-call run and a
   * finished edit look identical.
   */
  let sealed = false
  const pushCard = (
    card: ThreadCard,
    call?: ToolCall,
    message?: TranscriptMessage,
  ) => {
    // CONSECUTIVE CARDS OF ONE KIND ARE ONE CARD. A card earns its border by
    // being rare, and eleven identical "Edited a document" boxes in a row spend
    // it on repetition, which is the exact wall a tool run exists to prevent.
    // Same target and same verb only: "Wrote" beside "Edited" is two different
    // things that happened. Only calls group; an event (a session finishing, a
    // reply) always stands alone. Anything drawn in between, text or a tool
    // run, is a different last row and so ends the group without a flag.
    const last = rows.at(-1)
    // RFC 0348 3e: reports in a row are one group, whatever each one says.
    // A burst of agents stopping is one thing that happened to the thread.
    if (
      joinable() &&
      isStopReport(message) &&
      last?.kind === 'card' &&
      isStopReport(last.message)
    ) {
      last.more = [
        ...(last.more ?? []),
        { card, ...(message ? { message } : {}) },
      ]
      return
    }
    if (
      joinable() &&
      call &&
      // The agent's comments stay one row each: each went to a different
      // person or passage, and "Replied 3 times" would say to whom for none.
      card.target !== 'comment' &&
      last?.kind === 'card' &&
      last.call &&
      last.card.target === card.target &&
      last.card.verb === card.verb
    ) {
      last.more = [...(last.more ?? []), { card, call }]
      return
    }
    rows.push({
      kind: 'card',
      // Keyed on the CALL when there is one, so a card keeps its identity (and
      // any open state) as the transcript grows underneath it. An event has no
      // id of its own on the projection, so it falls back to a counter.
      key: call ? `c${call.id}` : `c${cardKey++}`,
      card,
      ...(call ? { call } : {}),
      ...(message ? { message } : {}),
    })
  }

  const pushCall = (call: ToolCall) => {
    const last = rows.at(-1)
    // AN EDIT IS NOT PART OF A RUN (RFC 0298 §4f). A run exists to absorb
    // repetition, and an edit is the thing that happened. Left in the group it
    // was unreachable: a group renders its children only while it is open, so
    // the edit never got the chance to open itself, and a run is the normal
    // case. Measured before this: 173 diff rows in one session, every one
    // behind a collapsed group.
    //
    // THROUGH `isEdit`, which is exactly the predicate that decides whether a
    // diff renders. Any other test here and the rows that break out would drift
    // from the rows that had something to show.
    if (isEdit(call.name, call.arguments)) {
      rows.push({ kind: 'tools', key: `t${call.id}`, calls: [call] })
      sealed = true
      return
    }
    // Adjacency is measured on the ROW list, not the message list: two calls in
    // different messages are still adjacent if nothing was drawn between them.
    if (joinable() && last?.kind === 'tools' && !sealed) {
      last.calls.push(call)
      return
    }
    rows.push({ kind: 'tools', key: `t${call.id}`, calls: [call] })
    sealed = false
  }

  // RFC 0301 §3f: a signal draws nothing, and its turn draws only if it spoke.
  // A comment is the exception: it draws, and says when it was only read.
  const passed = passedComments(transcript)
  const indexOf = new Map(transcript.map((m, i) => [m, i]))
  const place = (message: TranscriptMessage) => {
    // Results are not rows — they belong to the call they answer.
    if (message.role === 'tool_result') return

    // RFC 0307 3c: a spoken line joins the call it was said on. Read off
    // `spoken`, never the content: a line nobody relayed carries text written
    // for the agent, not the words as said.
    if (message.spoken) {
      const s = message.spoken
      const callId = argString(s.callId) ?? ''
      const line: SpokenLine = {
        key: message.entryId ? `s${message.entryId}` : `s${messageKey++}`,
        by: s.by === 'voice' ? 'voice' : 'person',
        text: argString(s.text) ?? '',
        relayed: s.relayed === true,
        ...(s.homeSessionId
          ? { homeSessionId: argString(s.homeSessionId) ?? '' }
          : {}),
        ...(s.homeEntryId
          ? { homeEntryId: argString(s.homeEntryId) ?? '' }
          : {}),
        ...(s.targetSessionId
          ? { targetSessionId: argString(s.targetSessionId) ?? '' }
          : {}),
      }
      const last = rows.at(-1)
      if (joinable() && last?.kind === 'call' && last.callId === callId)
        last.lines.push(line)
      else
        rows.push({ kind: 'call', key: `v${line.key}`, callId, lines: [line] })
      return
    }

    if (isCommentEvent(message)) {
      const note = passed.get(message)
      rows.push({
        kind: 'comment',
        key: message.entryId
          ? `k${message.entryId}`
          : `k${message.ref?.replyId ?? message.ref?.commentId ?? cardKey++}`,
        message,
        ...(note !== undefined ? { passed: note } : {}),
      })
      return
    }

    if (message.role !== 'assistant') {
      // A synthesised event (a session that finished, a comment answered, a
      // mention) becomes a card INSTEAD of its prose, not beside it. The prose
      // is written for the model — "read the whole reply with read_session on
      // that session id" — and showing both would put an instruction addressed
      // to the agent under a card addressed to the person.
      const event = cardForEvent(message)
      if (event) {
        pushCard(event, undefined, message)
        return
      }
      if (message.content.length > 0) pushMessage(message)
      return
    }

    let text: TranscriptBlock[] = []
    const flushText = () => {
      if (text.length === 0) return
      pushMessage({ ...message, content: text })
      text = []
    }

    for (const block of message.content) {
      if (block.type === 'attachment' || block.type === 'view_attachment')
        continue
      if (block.type === 'tool_use') {
        // The Git snapshot feeds the run Changes card. It has no separate row.
        if (block.name === 'runFileChanges') continue
        // Flush first: text written BEFORE a call stays before it, and ends the
        // run, so what the agent said is never hidden inside a collapsed group.
        flushText()
        const call = {
          id: block.id,
          name: block.name,
          arguments: block.arguments,
        }
        const card = cardForCall(call, resultFor?.(block.id))
        if (card) pushCard(card, call)
        else pushCall(call)
        continue
      }
      if (block.text.length > 0) text.push(block)
    }
    flushText()
  }
  // Turn starts, including prompts the thread hides, so a fence is raised even
  // when the prompt itself draws nothing.
  const turnStarts = transcript.flatMap((m, i) => (startsTurn(m) ? [i] : []))
  let nextStart = 0
  for (const message of hideQuietTurns(transcript)) {
    const at = indexOf.get(message)
    if (at !== undefined) {
      let crossed = false
      while ((turnStarts[nextStart] ?? Number.POSITIVE_INFINITY) <= at) {
        crossed = true
        nextStart++
      }
      if (crossed) floor = rows.length
    }
    const before = rows.length
    place(message)
    if (at === undefined) continue
    for (let r = before; r < rows.length; r++) (rows[r] as ThreadRow).at ??= at
  }

  // A run of reports too short to fold goes back to a card each.
  return rows.flatMap((row): ThreadRow[] => {
    if (
      row.kind !== 'card' ||
      !isStopReport(row.message) ||
      !row.more ||
      row.more.length + 1 >= STOP_GROUP_MIN
    )
      return [row]
    const { more, ...first } = row
    return [
      first,
      ...more.map(
        (m, i): ThreadRow => ({
          kind: 'card',
          key: `${row.key}.${i}`,
          ...(row.at !== undefined ? { at: row.at } : {}),
          card: m.card,
          ...(m.message ? { message: m.message } : {}),
        }),
      ),
    ]
  })
}
